import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCopy, Loader2, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { clinicOptionLabel, useIsInternalUser } from '@/components/ClinicLabel';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { useClinicianClinics, useClinicInvitationCode } from '@/hooks/queries';
import { extractApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import type { ClinicResponse } from '@/api/hospital';

/**
 * Clinic invitation-code dialog. Mirrors v1's `InviteCodeModal`
 * (clinician/src/components/addUser.tsx:207) but adds a clinic selector
 * so a clinician with access to multiple clinics can pick which code
 * to display rather than seeing all of them at once.
 *
 * UX details:
 *   • Single-clinic clinicians: selector is hidden; that clinic's code
 *     is fetched + shown on open.
 *   • Multi-clinic clinicians: clinic selector dropdown, defaulting to
 *     the first clinic in the list. Selection switches the displayed
 *     code; per-clinic responses are cached for 5 minutes by the hook.
 *   • Copy-to-clipboard with a transient "Copied" confirmation.
 *
 * The code is fetched lazily — the hook stays disabled until the dialog
 * opens. v1 fired one query per clinic on dialog open (parallel useQueries);
 * we fire only the one the user is looking at, which is cheaper.
 */
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const InvitationCodeDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const isInternal = useIsInternalUser();
  const clinician = useAuthStore((s) => s.clinician);
  // The clinician's "home" hospital_id — used to discover their clinics
  // and to bias the default selection toward in-hospital clinics. Internal
  // RxFood clinicians can have memberships across multiple hospitals; the
  // discovery endpoint returns ALL of them but each clinic carries its
  // own hospital_id which we use for the per-code fetch (mirroring v1's
  // InviteCodeModal at clinician/src/components/addUser.tsx:218).
  const homeHospitalId = clinicianHomeHospitalId(clinician) ?? null;
  const clinicianId = (clinician?.id as number | undefined) ?? null;

  const clinics = useClinicianClinics(clinicianId);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);

  /**
   * Sort clinics so the clinician's home-hospital clinics float to the
   * top. The default selection (below) picks the first row, which means
   * internal users automatically land on a clinic that matches their
   * profile's hospital_id instead of a random cross-hospital one. Within
   * each group we keep the server-returned order so admin-curated
   * sequences are preserved.
   */
  const sortedClinics = useMemo<ClinicResponse[]>(() => {
    const rows = clinics.data ?? [];
    if (homeHospitalId == null) return rows;
    return [...rows].sort((a, b) => {
      const aHome = a.hospital_id === homeHospitalId ? 0 : 1;
      const bHome = b.hospital_id === homeHospitalId ? 0 : 1;
      return aHome - bHome;
    });
  }, [clinics.data, homeHospitalId]);

  // Default the selection to the first clinic once the list loads — which
  // after the sort above is a home-hospital clinic when one exists. Manual
  // selection sticks across re-opens until the underlying list changes.
  useEffect(() => {
    if (sortedClinics.length === 0) return;
    if (selectedClinicId == null) {
      setSelectedClinicId(sortedClinics[0].id);
      return;
    }
    // If the previously-selected clinic is no longer in the list (clinician
    // was removed from it), fall back to the first available.
    if (!sortedClinics.some((c) => c.id === selectedClinicId)) {
      setSelectedClinicId(sortedClinics[0].id);
    }
  }, [sortedClinics, selectedClinicId]);

  // Look up the selected clinic's hospital_id — NOT the clinician's. For
  // internal users this can differ from `homeHospitalId`, and using the
  // wrong hospital in the path makes the code request 404 (or worse,
  // silently return a code for a clinic with the same id in the wrong
  // hospital, though typically those ids are globally unique).
  const selectedClinic = useMemo(
    () => sortedClinics.find((c) => c.id === selectedClinicId) ?? null,
    [sortedClinics, selectedClinicId],
  );
  const selectedHospitalId = selectedClinic?.hospital_id ?? homeHospitalId;

  const code = useClinicInvitationCode(selectedHospitalId, selectedClinicId, {
    enabled: open,
  });

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const handleCopy = async () => {
    const value = code.data?.invitation_code;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Silently fail — clipboard API can be blocked in non-secure contexts.
    }
  };

  const clinicsList = sortedClinics;
  const onlyOneClinic = clinicsList.length === 1;
  const selectedClinicName = selectedClinic?.name ?? '';
  // Show a subtle warning when the displayed code belongs to a clinic in
  // a different hospital than the clinician's profile. This is the
  // internal-user edge case the system normally disallows.
  const crossHospital =
    homeHospitalId != null
    && selectedClinic?.hospital_id != null
    && selectedClinic.hospital_id !== homeHospitalId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Clinic invitation code')}</DialogTitle>
          <DialogDescription>
            {t('Share this code with a patient so they can register and join your clinic.')}
          </DialogDescription>
        </DialogHeader>

        {clinics.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : clinicsList.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('No clinics found for this account.')}
          </p>
        ) : (
          <div className="space-y-4">
            {!onlyOneClinic && (
              <div className="space-y-1">
                <Label
                  htmlFor="invite-clinic"
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {t('Clinic')}
                </Label>
                <select
                  id="invite-clinic"
                  value={selectedClinicId ?? ''}
                  onChange={(e) => setSelectedClinicId(Number(e.target.value))}
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {clinicsList.map((c) => {
                    // Mark cross-hospital rows so internal users can tell at
                    // a glance which clinics live outside their primary
                    // hospital — only matters when both ids are known.
                    const isOtherHospital =
                      homeHospitalId != null
                      && c.hospital_id != null
                      && c.hospital_id !== homeHospitalId;
                    return (
                      <option key={c.id} value={c.id}>
                        {clinicOptionLabel(c.name, c.id, isInternal)}
                        {isOtherHospital && c.hospital_id != null
                          ? ` · ${t('hospital #{{id}}', { id: c.hospital_id })}`
                          : ''}
                      </option>
                    );
                  })}
                </select>
                {crossHospital && (
                  <p className="text-[0.6875rem] italic text-amber-700">
                    {t(
                      'This clinic belongs to a different hospital than your account.',
                    )}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {onlyOneClinic ? selectedClinicName : t('Invitation code')}
              </Label>
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  {code.isLoading ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('Loading…')}
                    </div>
                  ) : code.isError ? (
                    <p className="text-xs text-destructive">
                      {extractApiErrorMessage(code.error)
                        ?? t('Could not load invitation code.')}
                    </p>
                  ) : code.data?.invitation_code ? (
                    <p className="select-all break-all font-mono text-base tracking-wider">
                      {code.data.invitation_code}
                    </p>
                  ) : (
                    <p className="text-xs italic text-muted-foreground">
                      {t('— no code on file —')}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  disabled={!code.data?.invitation_code || code.isLoading}
                  aria-label={t('Copy invitation code')}
                  title={t('Copy invitation code')}
                  className={cn('h-8 w-8 shrink-0', copied && 'text-emerald-600')}
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
